import React from "react";
import { hydrateRoot } from "react-dom/client";
import { HydratedRouter } from "react-router/dom";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import * as Sentry from "@sentry/react";
import ReactGAModule from "react-ga4";
import { getRuntimeConfig } from "../src/runtimeConfig";

// Pre-hydration globals. country-profiles reads window.HERO_ELEMENT_NAME at
// import time, and the TTI observer must catch longtasks from page load — both
// need to run before React mounts.
window.HERO_ELEMENT_NAME = "";
window.HERO_ELEMENT_NAMES = [];
if ("performance" in window && performance.mark !== undefined) {
  performance.mark("atlas-page-load");
}
try {
  if ("PerformanceLongTaskTiming" in window) {
    const g: { e: PerformanceEntry[]; o?: PerformanceObserver } = {
      e: [],
    };
    (window as any).__tti = g;
    g.o = new PerformanceObserver((l) => {
      g.e = g.e.concat(l.getEntries());
    });
    g.o.observe({ entryTypes: ["longtask"] });
  }
} catch (e) {
  console.error(e);
}

const ReactGA = (ReactGAModule as any).default ?? ReactGAModule;

const config = getRuntimeConfig();

if (config.sentryDsn && config.sentryEnv) {
  Sentry.init({
    dsn: config.sentryDsn,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    environment: config.sentryEnv,
    tracesSampleRate: config.sentryTracesSampleRate ?? 0.8,
    replaysSessionSampleRate: config.sentryReplaysSessionSampleRate ?? 0.1,
    replaysOnErrorSampleRate: config.sentryReplaysOnErrorSampleRate ?? 1.0,
  });
} else {
  console.log("Build is running without Sentry.");
}

if (config.gaTrackingId) {
  ReactGA.initialize([
    {
      trackingId: config.gaTrackingId,
    },
  ]);
}

// Note: React.StrictMode is intentionally NOT used. Strict mode causes
// double-rendering which interferes with WebGL state management in the
// country-profiles visualizations.
hydrateRoot(document, <HydratedRouter />, {
  // Hydration mismatches are "recoverable" — React re-renders on the client
  // instead of throwing. On this app they can cascade into a fully unstyled
  // page (a body-level structural mismatch — e.g. a browser extension that
  // prepends a node to <body> — makes React tear down and re-render the whole
  // document, dropping emotion's injected styles). They're otherwise silent,
  // so log them to Sentry (already init'd above) to measure real frequency,
  // routes, and the component/selectors involved.
  onRecoverableError: (error, errorInfo) => {
    if (config.sentryDsn && config.sentryEnv) {
      Sentry.captureException(error, {
        level: "warning",
        tags: { hydration: "recoverable" },
        extra: {
          componentStack: errorInfo?.componentStack,
          route: window.location.pathname,
        },
      });
    }
    if (import.meta.env.DEV) {
      console.error("Recoverable hydration error", error, errorInfo);
    }
  },
});
